import * as React from "react";
import * as ReactDOM from "react-dom";
import { DefaultButton, PrimaryButton } from "OfficeFabric/Button";
import { Dialog, DialogFooter, DialogType } from "OfficeFabric/Dialog";
import { TextField } from "OfficeFabric/TextField";
import { KeyCode } from "VSS/Utils/UI";
import { Attachment } from "WorkItemTracking/Scripts/TFS.WorkItemTracking";
import { AttachmentsControlCIEvents } from "WorkItemTracking/Scripts/Utils/WorkItemTrackingCIEventHelper";
import * as WorkItemTrackingResources from "WorkItemTracking/Scripts/Resources/TFS.Resources.WorkItemTracking";

interface IEditCommentDialogProps {
    attachments: Attachment[];
    onClose: () => void;
}

class EditCommentDialog extends React.Component<IEditCommentDialogProps> {
    private _commentInput: string;
    private _dialogTitle: string;

    constructor(props: IEditCommentDialogProps) {
        super(props);

        const { attachments } = this.props;

        if (attachments && attachments.length === 1) {
            this._commentInput = attachments[0].getComment() || "";
            this._dialogTitle = WorkItemTrackingResources.EditComment;
        } else {
            this._commentInput = "";
            this._dialogTitle = WorkItemTrackingResources.EditComments;
        }
    }

    public render(): JSX.Element {
        return (
            <div onKeyDown={this._handleEscape}>
                <Dialog
                    className="edit-comment-dialog"
                    hidden={false}
                    onDismiss={this._closeDialog}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: this._dialogTitle
                    }}
                    modalProps={{
                        isBlocking: false
                    }}
                    firstFocusableSelector={WorkItemTrackingResources.EditCommentDialogLabel}
                >
                    <TextField
                        className={WorkItemTrackingResources.EditCommentDialogLabel}
                        label={WorkItemTrackingResources.EditCommentDialogLabel}
                        value={this._commentInput}
                        onChanged={newValue => {
                            this._commentInput = newValue;
                        }}
                        autoFocus={true}
                        maxLength={255}
                        onKeyDown={this._handleEnter}
                    />
                    <DialogFooter>
                        <PrimaryButton onClick={this._setComment} text={WorkItemTrackingResources.DialogSave} />
                        <DefaultButton onClick={this._closeDialog} text={WorkItemTrackingResources.Cancel} />
                    </DialogFooter>
                </Dialog>
            </div>
        );
    }

    private _closeDialog = (): void => {
        this.props.onClose && this.props.onClose();
    };

    private _setComment = (): void => {
        const { attachments } = this.props;

        /** Fire the change event only once even if setting comments on multiple attachments
         * to prevent invalidate being called on the grid for each attachment
         */
        for (let i = 0; i < attachments.length; i++) {
            const preventChangeEvent = i !== attachments.length - 1;
            attachments[i].setComment(this._commentInput, preventChangeEvent);
            // all the attachments have the same field ID, so firing on the last element is fine
        }

        AttachmentsControlCIEvents.publishEvent(AttachmentsControlCIEvents.ACTIONS_EDIT, {
            numOfSelectedAttachments: attachments.length,
            commentLength: this._commentInput.length
        });

        this._closeDialog();
    };

    private _handleEnter = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        // We only explicitly handle enter on the input, because it is implicitly treated as a click on the button.
        if (event.keyCode === KeyCode.ENTER) {
            this._setComment();
            event.stopPropagation();
            event.preventDefault();
        }
    };

    private _handleEscape = (event: React.KeyboardEvent<HTMLDivElement>): void => {
        if (event.keyCode === KeyCode.ESCAPE) {
            this._closeDialog();
            event.stopPropagation();
            event.preventDefault();
        }
    };
}

export function showEditCommentDialog(container: Element, attachments: Attachment[], onDialogClosed: () => void): void {
    ReactDOM.render(
        <EditCommentDialog
            attachments={attachments}
            onClose={() => {
                ReactDOM.unmountComponentAtNode(container);
                onDialogClosed();
            }}
        />,
        container
    );
}
